import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { IOpsLinkMessage, IOpsLinkTopic } from "@vmosolutions/oc-interfaces";
import _ from "lodash";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { IRootState } from "./CombinedReducers";
import { openSnackbar } from "../actions/App.Actions";

export interface IOpsLinkState {
  topicList: IOpsLinkTopic[];
  flightEvents: { [key: string]: any };
  submissionTime: Date | number;
}

const initialState: IOpsLinkState = {
  topicList: [],
  flightEvents: {},
  submissionTime: 0,
};

export const createEvent = createAsyncThunk(
  "OpsLink/createEvent",
  async (payload: { flightId: string; body: IOpsLinkMessage }, thunkApi) => {
    const { flightId, body } = payload;
    const notifications: INotifications = {
      dispatch: thunkApi.dispatch,
      infoMessage: "Creating new event...",
      successMessage: "Event created successfully",
      errorMessage: "Error: Unable to create event",
    };

    try {
      const response = await AmplifyAPIWrapper.Post<IOpsLinkMessage>(
        `/flights/opslink/events/${flightId}`,
        body,
        notifications
      );
      return response;
    } catch (e: any) {
      return e.response;
    }
  }
);

export const acknowledgeEvent = createAsyncThunk(
  "OpsLink/acknowledgeEvent",
  async (payload: { flightId: string; messageId: string }, thunkApi) => {
    const { flightId, messageId } = payload;
    const notifications: INotifications = {
      dispatch: thunkApi.dispatch,
      infoMessage: "Acknowledging message...",
      successMessage: "Message acknowledged successfully",
      errorMessage: "Error: Unable to acknowledge message",
    };

    try {
      const response = await AmplifyAPIWrapper.Put<IOpsLinkMessage>(
        `/flights/opslink/events/${flightId}/${messageId}/acknowledge`,
        {},
        notifications
      );
      return { flightId, response };
    } catch (e: any) {
      return thunkApi.rejectWithValue(e.response);
    }
  }
);

export const getTopics = createAsyncThunk("OpsLink/getTopics", async (_, thunkApi) => {
  try {
    const pulledTopics = await AmplifyAPIWrapper.Get<IOpsLinkTopic[]>("/flights/opslink/topics");
    return pulledTopics;
  } catch (e: any) {
    return e.response;
  }
});

export const getEventsByFlight = createAsyncThunk(
  "OpsLink/getEventsByFlight",
  async (payload: { flightId: string }, thunkApi) => {
    const { flightId } = payload;
    try {
      const pulledEvents = await AmplifyAPIWrapper.Get<IOpsLinkMessage[]>(`/flights/opslink/events/${flightId}`);
      return {
        flightId: flightId,
        events: pulledEvents,
      };
    } catch (e: any) {
      return e.response;
    }
  }
);

const updateEvents = async (currentFlightEvents: any) => {
  const newEvents: {
    [key: string]: any;
  } = {};
  for (const [key, val] of Object.entries(currentFlightEvents)) {
    const newEvent = await AmplifyAPIWrapper.Get<IOpsLinkMessage[]>(`/flights/opslink/events/${key}`);
    newEvents[key] = newEvent;
  }
  return newEvents;
};

export const refreshFlightEvents = createAsyncThunk(
  "OpsLink/refreshFlightEvents",
  async (notify: boolean, thunkApi) => {
    const rootState = thunkApi.getState() as IRootState;
    const currentFlightEvents = _.cloneDeep(rootState.OpsLinkReducer.flightEvents);
    const dispatch = thunkApi.dispatch;
    try {
      notify &&
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "info",
            message: "Refreshing events on all tracked flights...",
          })
        );
      //@ts-ignore
      for (const [key, val] of Object.entries(currentFlightEvents)) {
        const newEvents = await AmplifyAPIWrapper.Get<IOpsLinkMessage[]>(`/flights/opslink/events/${key}`);
        currentFlightEvents[key] = newEvents;
      }
      notify &&
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "success",
            message: "Events refreshed successfully.",
          })
        );
      return currentFlightEvents;
    } catch (e: any) {
      notify &&
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "error",
            message: "There was a problem refreshing flight events.",
          })
        );
      return e.response;
    }
  }
);

const opsLinkSlice = createSlice({
  name: "OpsLink",
  initialState,
  reducers: {
    /** Remove flight events for the given flight IDs (e.g. flights no longer on the dispatch desk). */
    removeFlightEvents(state, action: PayloadAction<string[]>) {
      for (const flightId of action.payload) {
        delete state.flightEvents[flightId];
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getTopics.fulfilled, (state, action: PayloadAction<IOpsLinkTopic[]>) => {
        if (action.payload.length > 0) {
          state.topicList = [...action.payload];
        }
      })
      .addCase(getEventsByFlight.fulfilled, (state, action: PayloadAction<any>) => {
        const { flightId, events } = action.payload;
        if (!state.flightEvents[flightId]) {
          state.flightEvents[flightId] = events;
        } else {
          state.flightEvents[flightId] = { ...state.flightEvents[flightId], ...events };
        }
      })
      .addCase(refreshFlightEvents.fulfilled, (state, action: PayloadAction<any>) => {
        state.flightEvents = action.payload;
      })
      .addCase(createEvent.fulfilled, (state, action: PayloadAction<any>) => {
        state.submissionTime = new Date();
      })
      .addCase(acknowledgeEvent.fulfilled, (state, action: PayloadAction<any>) => {
        state.submissionTime = new Date();
      });
  },
});

export const { removeFlightEvents } = opsLinkSlice.actions;
export default opsLinkSlice.reducer;
